/**
 * Trace/span id generation and time conversion.
 *
 * Isomorphic: `crypto.getRandomValues` exists in both the Workers runtime and
 * every browser we support, so this file has no environment branches.
 */

const HEX = '0123456789abcdef';

function randomHex(byteLength: number): string {
	const bytes = new Uint8Array(byteLength);
	crypto.getRandomValues(bytes);
	// Manual hex encode: toString(16) drops leading zeros per byte, which would
	// produce short ids that the collector silently discards.
	let out = '';
	for (let i = 0; i < bytes.length; i++) {
		const b = bytes[i]!;
		out += HEX[b >> 4]! + HEX[b & 0x0f]!;
	}
	return out;
}

/** 16 bytes -> 32 lowercase hex chars. */
export function newTraceId(): string {
	return randomHex(16);
}

/** 8 bytes -> 16 lowercase hex chars. */
export function newSpanId(): string {
	return randomHex(8);
}

const TRACE_ID_RE = /^[0-9a-f]{32}$/;
const SPAN_ID_RE = /^[0-9a-f]{16}$/;

/** All-zero ids are reserved as "invalid" by the spec and are dropped. */
const INVALID_TRACE_ID = '0'.repeat(32);
const INVALID_SPAN_ID = '0'.repeat(16);

export function isValidTraceId(id: string): boolean {
	return TRACE_ID_RE.test(id) && id !== INVALID_TRACE_ID;
}

export function isValidSpanId(id: string): boolean {
	return SPAN_ID_RE.test(id) && id !== INVALID_SPAN_ID;
}

/**
 * Milliseconds -> unix nanoseconds as a decimal string.
 *
 * Done by string concatenation rather than `ms * 1e6` because the product
 * exceeds Number.MAX_SAFE_INTEGER (1.76e12 ms * 1e6 ≈ 1.76e18 > 9.0e15), so
 * float multiplication would quietly lose the low-order digits.
 */
export function msToNanoString(ms: number): string {
	const whole = Math.floor(ms);
	const fractionalNanos = Math.round((ms - whole) * 1e6);
	if (fractionalNanos <= 0) return `${whole}000000`;
	// Pad the sub-millisecond remainder to exactly 6 digits.
	return `${whole}${String(Math.min(fractionalNanos, 999999)).padStart(6, '0')}`;
}
